import {i18n} from '@online-offer/i18n';
import {Grid, GridItem} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {any, values} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish, parseDate} from 'shared/web';

import {DataColumn, DataColumnItem} from '../../components/DataColumn/DataColumn';
import {SectionPrint} from '../../components/SectionPrint/SectionPrint';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getDataForDetails} from '../../utils/getDataForDetails';
import {ElectricalConsumptionData, FuelConsumptionData} from '../../utils/getFuelConsumptionData';

function getFuelConsumptionItems(
  fuelConsumptionData: FuelConsumptionData | Nullish
): DataColumnItem[] {
  if (!fuelConsumptionData) {
    return [];
  }

  return match(fuelConsumptionData)
    .with({schema: 'WLTP'}, (wltpData) => [
      {label: i18n.t('details.fuelConsumption.wltp.combined'), value: wltpData.combined},
      {label: i18n.t('details.fuelConsumption.wltp.cityCenter'), value: wltpData.cityCenter},
      {label: i18n.t('details.fuelConsumption.wltp.outskirts'), value: wltpData.outskirts},
      {label: i18n.t('details.fuelConsumption.wltp.countryRoads'), value: wltpData.countryRoads},
      {label: i18n.t('details.fuelConsumption.wltp.highway'), value: wltpData.highway},
      {label: i18n.t('details.fuelConsumption.wltp.weighted'), value: wltpData.weighted},
    ])
    .with({schema: 'NEDC'}, (nedcData) => [
      {label: i18n.t('details.fuelConsumption.nedc.combined'), value: nedcData.combined},
      {label: i18n.t('details.fuelConsumption.nedc.urban'), value: nedcData.urban},
      {label: i18n.t('details.fuelConsumption.nedc.extraUrban'), value: nedcData.extraUrban},
    ])
    .exhaustive();
}

function getElectricalConsumptionItems(
  electricalConsumptionData: ElectricalConsumptionData | Nullish
): DataColumnItem[] {
  if (!electricalConsumptionData) {
    return [];
  }

  return match(electricalConsumptionData)
    .with({schema: 'WLTP'}, (wltpData) => [
      {label: i18n.t('details.electricalConsumption.wltp.combined'), value: wltpData.combined},
      {label: i18n.t('details.electricalConsumption.wltp.cityCenter'), value: wltpData.cityCenter},
      {label: i18n.t('details.electricalConsumption.wltp.outskirts'), value: wltpData.outskirts},
      {
        label: i18n.t('details.electricalConsumption.wltp.countryRoads'),
        value: wltpData.countryRoads,
      },
      {label: i18n.t('details.electricalConsumption.wltp.highway'), value: wltpData.highway},
      {
        label: i18n.t('details.electricalConsumption.wltp.weightedCombined'),
        value: wltpData.weightedCombined,
      },
    ])
    .with({schema: 'NEDC'}, (nedcData) => [
      {label: i18n.t('details.electricalConsumption.nedc.combined'), value: nedcData.combined},
    ])
    .exhaustive();
}

export function DetailsPrint() {
  const {vehicle, alphaCatalogue} = useGetOnlineOfferData();
  const formatDateTime = useDateTimeFormatter();

  if (!vehicle) {
    return null;
  }

  const data = getDataForDetails(vehicle, alphaCatalogue);

  if (!data || !any(isNotNilOrEmpty, values(data))) {
    return null;
  }

  const fuelConsumptionItems = getFuelConsumptionItems(data.fuelConsumptionData);
  const electricalConsumptionItems = getElectricalConsumptionItems(data.electricalConsumptionData);

  return (
    <SectionPrint heading={i18n.t('details.heading')}>
      <Grid columns={2} spacing={8}>
        <GridItem>
          <DataColumn
            data={[
              {label: i18n.t('details.vin'), value: data.vin},
              {
                label: i18n.t('details.firstRegistration'),
                value: data.firstRegistration
                  ? formatDateTime('dateShort', parseDate(data.firstRegistration))
                  : '',
              },
              {label: i18n.t('details.countryOfOrigin'), value: data.countryOfOrigin},
              {label: i18n.t('details.typeOfColour'), value: data.typeOfColour},
              {label: i18n.t('details.manufacturerColourName'), value: data.manufacturerColourName},
              {
                label: i18n.t('details.exteriorColour'),
                value: data.exteriorColour?.name,
                colorCode: data.exteriorColour?.code ?? undefined,
              },
              {
                label: i18n.t('details.interiorColour'),
                value: data.interiorColour?.name,
                colorCode: data.interiorColour?.code ?? undefined,
              },
              {
                label: i18n.t('details.interiorColourName'),
                value: data.interiorColourName,
              },
              {label: i18n.t('details.interiorMaterial'), value: data.interiorMaterial},
              {label: i18n.t('details.numberOfSeats'), value: data.numberOfSeats},
              {label: i18n.t('details.numberOfDoors'), value: data.numberOfDoors},
              {label: i18n.t('details.numberOfKeys'), value: data.numberOfKeys},
              {label: i18n.t('details.numberOfOwners'), value: data.numberOfOwners},
            ]}
          />
        </GridItem>
        <GridItem>
          <DataColumn
            data={[
              {label: i18n.t('details.fuel'), value: data.fuelType},
              {label: i18n.t('details.secondaryFuel'), value: data.secondaryFuelType},
              {label: i18n.t('details.hybridType'), value: data.hybridType},
              {label: i18n.t('details.emissionClass'), value: data.emissionClass},
              {
                label: i18n.t('details.enginePower'),
                value: data.combustionEnginePower,
                labelTooltip: i18n.t('details.enginePowerTooltip'),
              },
              {
                label: i18n.t('details.electricalEnginePower'),
                value: data.electricalEnginePower,
                labelTooltip: i18n.t('details.electricalEnginePowerTooltip'),
              },
              {
                label: i18n.t('details.maximalPower'),
                value: data.maximalEnginePower,
                labelTooltip: i18n.t('details.maximalPowerTooltip'),
              },
              {label: i18n.t('details.engineDisplacement'), value: data.engineDisplacement},
              {label: i18n.t('details.numberOfPistons'), value: data.numberOfPistons},
              {label: i18n.t('details.numberOfGears'), value: data.numberOfGears},
              ...fuelConsumptionItems,
              ...electricalConsumptionItems,
            ]}
          />
        </GridItem>
      </Grid>
    </SectionPrint>
  );
}
